import { Box, Heading, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { FaqAccordion, type FaqAccordionItem } from '#features/faq/components/FaqAccordion';
import { FAQ_ITEMS } from '#features/faq/data/faqData';

type FAQProps = {
  gameName: string;
};

export const FAQ = ({ gameName }: FAQProps) => {
  const { t, i18n } = useLingui();
  // `undefined` = initial (first item open); a string/null tracks explicit user choice.
  const [openKey, setOpenKey] = useState<string | null | undefined>(undefined);

  const items = useMemo<FaqAccordionItem[]>(() => {
    const base: FaqAccordionItem[] = FAQ_ITEMS.filter(
      (item) => item.showOnLanding && !(gameName && item.hideOnGamePages)
    ).map((item) => ({
      key: item.id,
      question: i18n._(item.question),
      answer: i18n._(item.answer),
    }));

    if (gameName) {
      // Inline macro interpolation is the reliable way to inject the game name:
      // the Lingui macro keeps the message in the catalog AND binds the value on
      // both server and client (a plain t`id`.replaceAll strips the placeholder
      // after hydration, and a raw i18n._(id, values) misses it on the client).
      // These game-specific Q&A also target the "team finder" / "duo" search
      // patterns (high-CTR in GSC) and feed the per-page FAQPage JSON-LD below.
      const gameItems: FaqAccordionItem[] = [
        {
          key: 'game-teammates',
          question: t`How do I find ${gameName} teammates?`,
          answer: t`Pick ${gameName}, choose your platform and available game options, then post your LFG or browse live posts. Most players find ${gameName} teammates within minutes - completely free.`,
        },
        {
          key: 'game-team-finder',
          question: t`Is there a ${gameName} team finder?`,
          answer: t`Yes - Teamplay is a free ${gameName} team finder. Filter players by platform and available game options, post your LFG, and get matched with teammates who fit how you play. No fees, no Discord server hopping.`,
        },
        {
          key: 'game-duo',
          question: t`How do I find a ${gameName} duo partner?`,
          answer: t`Post a ${gameName} LFG to find a duo, or browse players looking for a duo right now. Filter by platform and available game options to find a partner who fits how you play, then squad up and jump into voice in one click.`,
        },
      ];
      base.unshift(...gameItems);
    }

    return base;
  }, [t, i18n, gameName]);

  const openedKey = openKey === undefined ? (items[0]?.key ?? null) : openKey;
  const handleToggle = (key: string) =>
    setOpenKey((prev) => {
      const current = prev === undefined ? items[0]?.key : prev;
      return current === key ? null : key;
    });

  // Canonical FAQ schema lives on /faq. On game landing pages we still emit it,
  // because the game-specific question is unique to that URL; on the generic
  // index we omit it to avoid duplicating /faq's structured data.
  const jsonLd = useMemo(
    () =>
      gameName
        ? {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: items.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer },
            })),
          }
        : null,
    [items, gameName]
  );

  return (
    <Box as="section" aria-label="FAQ" w="100%">
      {jsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      <Box textAlign="center" mb={{ base: 8, lg: 10 }}>
        <Text
          fontSize="xs"
          fontWeight="700"
          color="purple.300"
          textTransform="uppercase"
          letterSpacing="0.15em"
          mb={3}
        >
          FAQ
        </Text>
        <Heading
          fontWeight="900"
          lineHeight="1.05"
          fontSize={{ base: '3xl', lg: '5xl' }}
          as="h2"
          textTransform="uppercase"
          letterSpacing="-0.02em"
        >
          {t`v2.faq.title`}
        </Heading>
      </Box>

      <Box maxW="800px" mx="auto">
        <FaqAccordion items={items} openKey={openedKey} onToggle={handleToggle} />
      </Box>
    </Box>
  );
};
